import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Text,
  Paper,
  Button,
  Group,
  Loader,
  Alert,
  Stack,
  Card,
  Badge,
  ScrollArea,
  Textarea,
  ActionIcon,
  Anchor,
  Tooltip,
} from '@mantine/core'
import {
  IconFilter,
  IconMessages,
  IconAlertCircle,
  IconSend,
  IconExternalLink,
} from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listGenieSpaces,
  isGenieSpaceAvailable,
  startGenieConversation,
  sendGenieMessage,
  getGenieMessage,
  isGenieMessageTerminal,
  extractGenieReplyText,
  findGenieQueryAttachment,
  listGenieConversationMessages,
  getGenieSpaceUrl,
  type GenieConversationMessage,
  type GenieConversationSummary,
  type GenieQueryInfo,
} from '../../api/genie'
import { GenieQueryResultView } from './GenieQueryResult'
import { GenieConversationHistory } from './GenieConversationHistory'

interface ChatEntry {
  role: 'user' | 'assistant'
  content: string
  timestamp: number
  query?: GenieQueryInfo
}

// Genie processes each question asynchronously (it runs a query against the
// data), so a newly created message starts out in a non-terminal status
// (e.g. IN_PROGRESS). If the space's SQL warehouse is stopped, Genie may
// also need to wait for it to start before it can even begin executing,
// which can itself take a minute or more. Poll for up to ~3 minutes before
// giving up.
const POLL_INTERVAL_MS = 2000
const MAX_POLL_ATTEMPTS = 90

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function formatGenieStatus(status: string): string {
  switch (status) {
    case 'FETCHING_METADATA':
      return 'Reviewing table metadata…'
    case 'FILTERING_CONTEXT':
      return 'Narrowing down relevant tables…'
    case 'PENDING_WAREHOUSE':
      return 'Waiting for the SQL warehouse to start…'
    case 'EXECUTING_QUERY':
      return 'Running the query…'
    case 'SUBMITTED':
    case 'IN_PROGRESS':
    case 'ASKING_AI':
      return 'Genie is thinking…'
    default:
      return `Genie is thinking… (${status})`
  }
}

/**
 * Convert one Genie conversation message (a full turn -- the user's
 * question plus, once COMPLETED, Genie's response in the same object) into
 * the ChatEntry/ies used to render it.
 */
function messageToChatEntries(message: GenieConversationMessage): ChatEntry[] {
  const entries: ChatEntry[] = []

  if (message.content) {
    entries.push({
      role: 'user',
      content: message.content,
      timestamp: 0,
    })
  }

  if (isGenieMessageTerminal(message.status)) {
    entries.push({
      role: 'assistant',
      content: extractGenieReplyText(message),
      timestamp: 0,
      query: findGenieQueryAttachment(message) ?? undefined,
    })
  }

  return entries
}

async function pollForCompletion(
  spaceId: string,
  conversationId: string,
  messageId: string,
  onStatusChange?: (status: string) => void
): Promise<GenieConversationMessage> {
  let message: GenieConversationMessage | null = null
  let lastStatus: string | undefined

  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    message = await getGenieMessage(spaceId, conversationId, messageId)

    if (message.status !== lastStatus) {
      lastStatus = message.status
      onStatusChange?.(message.status)
    }

    if (isGenieMessageTerminal(message.status)) {
      return message
    }

    if (attempt < MAX_POLL_ATTEMPTS - 1) {
      await sleep(POLL_INTERVAL_MS)
    }
  }

  throw new Error(
    `Genie is taking longer than expected to respond (last status: ${
      lastStatus ?? 'unknown'
    }). Please try again in a moment.`
  )
}

export function GenieChat() {
  const queryClient = useQueryClient()
  const [showAllAvailable, setShowAllAvailable] = useState(false)
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null)

  const [conversationId, setConversationId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatEntry[]>([])
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [chatError, setChatError] = useState<string | null>(null)
  const [sendingStatus, setSendingStatus] = useState<string | null>(null)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)

  const viewportRef = useRef<HTMLDivElement>(null)

  const {
    data: spaces = [],
    isLoading,
    isError,
    error,
  } = useQuery(['genie', 'spaces'], listGenieSpaces, { retry: 1 })

  const availableSpaces = useMemo(
    () => spaces.filter(isGenieSpaceAvailable),
    [spaces]
  )
  const selectedSpace = availableSpaces.find(
    (space) => space.space_id === selectedSpaceId
  )
  const genieSpaceUrl = selectedSpace
    ? getGenieSpaceUrl(selectedSpace.space_id)
    : null
  const displayedSpaces = showAllAvailable
    ? availableSpaces
    : selectedSpace
      ? [selectedSpace]
      : []

  useEffect(() => {
    const preferred = import.meta.env.VITE_GENIE_SPACE_ID
    const match = preferred
      ? availableSpaces.find((space) => space.space_id === preferred)
      : undefined
    setSelectedSpaceId(match?.space_id || availableSpaces[0]?.space_id || null)
  }, [availableSpaces])

  const startNewChat = useCallback(() => {
    setConversationId(null)
    setMessages([])
    setChatError(null)
    setInput('')
  }, [])

  // Start a fresh conversation whenever the selected space changes.
  useEffect(() => {
    startNewChat()
  }, [selectedSpaceId, startNewChat])

  useEffect(() => {
    viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight })
  }, [messages, isSending])

  const handleSelectConversation = async (
    conversation: GenieConversationSummary
  ) => {
    if (!selectedSpace || conversation.id === conversationId) return

    setChatError(null)
    setIsLoadingHistory(true)
    try {
      const rawMessages = await listGenieConversationMessages(
        selectedSpace.space_id,
        conversation.id
      )
      setMessages(rawMessages.flatMap(messageToChatEntries))
      setConversationId(conversation.id)
      setInput('')
    } catch (err) {
      setChatError(
        err instanceof Error
          ? err.message
          : 'Failed to load this past conversation.'
      )
    } finally {
      setIsLoadingHistory(false)
    }
  }

  const handleSend = async () => {
    const content = input.trim()
    if (!content || !selectedSpace || isSending || isLoadingHistory) return

    const spaceId = selectedSpace.space_id
    setInput('')
    setChatError(null)
    setMessages((prev) => [
      ...prev,
      { role: 'user', content, timestamp: Date.now() },
    ])
    setIsSending(true)
    setSendingStatus(null)

    try {
      let activeConversationId = conversationId
      let messageId: string

      if (activeConversationId) {
        messageId = await sendGenieMessage(
          spaceId,
          activeConversationId,
          content
        )
      } else {
        const started = await startGenieConversation(spaceId, content)
        activeConversationId = started.conversationId
        messageId = started.messageId
        setConversationId(activeConversationId)
        // A brand-new conversation now exists in this space -- refresh the
        // past-chats list so it shows up without a manual reload.
        queryClient.invalidateQueries(['genie', 'conversations', spaceId])
      }

      const finalMessage = await pollForCompletion(
        spaceId,
        activeConversationId,
        messageId,
        setSendingStatus
      )

      if (finalMessage.status === 'COMPLETED') {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: extractGenieReplyText(finalMessage),
            timestamp: Date.now(),
            query: findGenieQueryAttachment(finalMessage) ?? undefined,
          },
        ])
        // The conversation's title/last-updated time may only settle once
        // Genie finishes responding -- refresh the past-chats list again.
        queryClient.invalidateQueries(['genie', 'conversations', spaceId])
      } else {
        setChatError(
          finalMessage.error?.message ||
            `Genie could not answer this question (status: ${finalMessage.status}).`
        )
      }
    } catch (err) {
      setChatError(
        err instanceof Error ? err.message : 'Failed to send message.'
      )
    } finally {
      setIsSending(false)
      setSendingStatus(null)
    }
  }

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 140px)',
      }}
    >
      <Group justify="space-between" mb="md">
        <Box>
          <Text size="xl" fw={700}>
            Chat with your data
          </Text>
          <Text size="sm" c="dimmed">
            Ask questions about Prism data using the Databricks Genie
            Conversation API, authenticated with your Microsoft account.
          </Text>
        </Box>
        {availableSpaces.length > 1 && (
          <Button
            variant={showAllAvailable ? 'filled' : 'light'}
            color="prism"
            leftSection={<IconFilter size={16} />}
            onClick={() => setShowAllAvailable((value) => !value)}
          >
            {showAllAvailable
              ? 'Hide Genie spaces available'
              : 'Show all Genie spaces available'}
          </Button>
        )}
      </Group>

      {isLoading && (
        <Box
          style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}
        >
          <Loader color="prism" />
        </Box>
      )}

      {isError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          title="Could not load Genie spaces"
          mb="md"
        >
          {(error as Error)?.message ?? 'Request failed'}
        </Alert>
      )}

      {!isLoading && !isError && (
        <Stack gap="md" mb="md">
          {displayedSpaces.length === 0 ? (
            <Text c="gray.5">No Genie spaces found.</Text>
          ) : (
            displayedSpaces.map((space) => (
              <Card key={space.space_id} withBorder shadow="sm" p="md">
                <Group justify="space-between" align="flex-start">
                  <Box>
                    <Text fw={600}>
                      {space.title || space.display_name || space.space_id}
                    </Text>
                    {space.description && (
                      <Text size="sm" c="dimmed" lineClamp={2}>
                        {space.description}
                      </Text>
                    )}
                    {space.state && (
                      <Badge
                        size="sm"
                        mt="xs"
                        color={isGenieSpaceAvailable(space) ? 'green' : 'gray'}
                        variant="light"
                      >
                        {space.state}
                      </Badge>
                    )}
                  </Box>
                  <Button
                    variant={
                      selectedSpaceId === space.space_id ? 'filled' : 'light'
                    }
                    color="prism"
                    leftSection={<IconMessages size={16} />}
                    onClick={() => {
                      setSelectedSpaceId(space.space_id)
                      setShowAllAvailable(false)
                    }}
                  >
                    {selectedSpaceId === space.space_id ? 'Selected' : 'Chat'}
                  </Button>
                </Group>
              </Card>
            ))
          )}
        </Stack>
      )}

      {!isLoading &&
        !isError &&
        displayedSpaces.length > 0 &&
        !selectedSpace && (
          <Paper
            withBorder
            radius="md"
            p="xl"
            mb="md"
            style={{ textAlign: 'center' }}
          >
            <Text c="gray.5">Select a Genie space to start chatting.</Text>
          </Paper>
        )}

      {selectedSpace && (
        <Paper
          withBorder
          radius="md"
          flex={1}
          style={{
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          <GenieConversationHistory
            spaceId={selectedSpace.space_id}
            activeConversationId={conversationId}
            onSelectConversation={handleSelectConversation}
            onNewChat={startNewChat}
          />

          <Box
            flex={1}
            style={{
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Group
              justify="space-between"
              px="md"
              py="xs"
              style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
            >
              <Text size="sm" fw={600} c="gray.7" lineClamp={1}>
                {selectedSpace.title ||
                  selectedSpace.display_name ||
                  selectedSpace.space_id}
              </Text>
              {genieSpaceUrl && (
                <Tooltip
                  label="Open this Genie space in Databricks"
                  withArrow
                  position="left"
                >
                  <Anchor
                    href={genieSpaceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open this Genie space in Databricks"
                  >
                    <IconExternalLink
                      size={16}
                      color="var(--mantine-color-gray-5)"
                    />
                  </Anchor>
                </Tooltip>
              )}
            </Group>
            <ScrollArea flex={1} p="md" viewportRef={viewportRef}>
              {isLoadingHistory ? (
                <Group justify="center" py="xl">
                  <Loader size="sm" color="prism" />
                  <Text c="dimmed">Loading conversation…</Text>
                </Group>
              ) : messages.length === 0 ? (
                <Text c="gray.5" ta="center" mt="xl">
                  Ask a question about{' '}
                  {selectedSpace.title ||
                    selectedSpace.display_name ||
                    'this Genie space'}
                  .
                </Text>
              ) : (
                <Stack gap="sm">
                  {messages.map((message, index) => (
                    <Box
                      key={index}
                      style={{
                        alignSelf:
                          message.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: message.query ? '100%' : '75%',
                        width: message.query ? '100%' : undefined,
                      }}
                    >
                      <Paper
                        p="sm"
                        radius="md"
                        bg={message.role === 'user' ? 'prism.6' : 'gray.1'}
                        c={message.role === 'user' ? 'white' : 'gray.9'}
                      >
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                          {message.content}
                        </Text>
                        {message.query && (
                          <GenieQueryResultView
                            statementId={message.query.statementId}
                            sql={message.query.query}
                          />
                        )}
                      </Paper>
                    </Box>
                  ))}
                </Stack>
              )}
              {isSending && (
                <Group gap="xs" mt="sm">
                  <Loader size="xs" color="prism" />
                  <Text size="xs" c="dimmed">
                    {sendingStatus
                      ? formatGenieStatus(sendingStatus)
                      : 'Genie is thinking…'}
                  </Text>
                </Group>
              )}
            </ScrollArea>

            {chatError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                mx="md"
                mb="sm"
              >
                {chatError}
              </Alert>
            )}

            <Group
              p="md"
              gap="sm"
              align="flex-end"
              style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}
            >
              <Textarea
                flex={1}
                placeholder="Ask a question about your data…"
                autosize
                minRows={1}
                maxRows={4}
                value={input}
                onChange={(event) => setInput(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    handleSend()
                  }
                }}
                disabled={isSending || isLoadingHistory}
              />
              <ActionIcon
                size="lg"
                color="prism"
                variant="filled"
                onClick={handleSend}
                disabled={!input.trim() || isSending || isLoadingHistory}
                loading={isSending}
              >
                <IconSend size={18} />
              </ActionIcon>
            </Group>
          </Box>
        </Paper>
      )}
    </Box>
  )
}
